import React, { useState, useEffect, useRef } from "react";
import PageHeader from "../../components/global-layout/page-header";
import {
  DashboardPageHeaderIcon,
  CardIconInspectionSearch,
  RefreshIcon,
  PageModalCloseIcon,
} from "../../components/svg";
import { ChevronRight, ChevronLeft, ExpandIcon } from "lucide-react";
import IconTextButton from "../../components/form/icon-text-btn";
import Skeleton from "react-loading-skeleton";
import {
  BarChart,
  LineChart,
  ChartContainer,
  ChartsTooltip,
  BarSeriesType,
} from "@mui/x-charts";
import { BarPlot, BarLabelProps } from "@mui/x-charts/BarChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { styled } from "@mui/material/styles";
import * as d3 from "d3-scale-chromatic";
import SelectDuration from "../../components/UI/select-duration/select-duration";
import { dashboard } from "../../services/InspectionOneServices";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MultipleRadialBars from "./MultipleRadialBars";
import { useMyContext } from "../../components/my-context/MyContext";
import { useTranslation } from "react-i18next";
import ScrollableCardBlock from "./scrollableCardBlock";
import Authentication from "../../auth/Authentication";
import IconBtn from "../../components/form/icon-btn";
import { baseApiUrlList } from "../../services/baseUrl";
import { mapDynmaicURL } from "../../utils/common";

// import Authentication from "../../auth/Authentication";
// const authService = Authentication();
// Types
interface Option {
  id: number;
  value: string;
  label: string;
}

interface LineChartData {
  label: string;
  data: {
    status: string;
    value: number;
  }[];
}
interface CardData {
  status: string;
  value: number;
  backgroundColor: string;
  fontColor: string;
  imageUrl: string;
}
interface DashboardSiteData {
  siteName: string;
  cardsData: CardData[];
  lineChartData: LineChartData[];
}
type DashboardResponse = {
  dashboards: DashboardSiteData[];
};

const ReactDashboardUI: React.FC = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lineChartData, setLineChartData] = useState<LineChartData[]>([]);
  const [dataKey, setDataKeys] = useState<string[]>([]);
  const [cardsData, setCardsData] = useState<DashboardSiteData[]>([]);
  const [expandedSite, setExpandedSite] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const cardContainerRef = useRef<HTMLDivElement | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [isBarChartModalOpen, setIsBarChartModalOpen] = useState(false);
  const [barWithValue, setBarWithValue] = useState<any>(0);
  const [chartWidth, setChartWidth] = useState(0);
  const { pageTitle } = useMyContext();
  const { t } = useTranslation();
  const authService = Authentication();

  const durationOptions: Option[] = [
    { id: 1, value: "noFilter", label: "All time" },
    { id: 2, value: "today", label: "Today" },
    { id: 3, value: "thisWeek", label: "This Week" },
    { id: 4, value: "thisMonth", label: "This Month" },
    { id: 5, value: "thisYear", label: "This Year" },
    { id: 6, value: "custom", label: "Custom" },
  ];
  const [selectedOption, setSelectedOption] = useState<Option>(
    durationOptions[0]
  );

  const [containerWidth, setContainerWidth] = useState(1000);
  const dataKeys = lineChartData?.[0]?.data
    ? Object.keys(lineChartData[0].data)
    : [];

  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const getIconForStatus = (status: string) => {
    const safeStatus = status?.toLowerCase();

    switch (safeStatus) {
      case "total":
        return <CardIconInspectionSearch />;
      default:
        return <CardIconInspectionSearch />;
    }
  };

  const colorMap: { [key: string]: string } = {};
  cardsData.forEach((site) => {
    site.cardsData.forEach((card) => {
      colorMap[card.status] = card.fontColor;
    });
  });

  // Icon Background Color
  const getIconBackgroundColor = (label: string, isTotal: boolean) => {
    if (isTotal) return "bg-white bg-opacity-20";
    switch (label) {
      case "Pass":
        return "bg-[#06D6A0]";
      case "Fail":
        return "bg-[#EF476F]";
      case "Overridden":
        return "bg-[#FFD166]";
      case "Pending":
        return "bg-[#FF8225]";
      case "In-Progress":
        return "bg-[#FFEB00]";
      case "Completed":
        return "bg-[#344CB7]";
      case "Negative":
        return "bg-[#EB3678]";
      case "Un-Verified":
        return "bg-[#C8ACD6]";
      case "Verified":
        return "bg-[#3FA2F6]";
      default:
        return "bg-gray-200";
    }
  };
  const getDateRangeUtc = (
    dateFilterId: number,
    startDate?: any,
    endDate?: any
  ): { fromDateUtc: string; toDateUtc: string } => {
    const localNow = new Date();
    let fromDateUtc = "";
    let toDateUtc = "";

    switch (dateFilterId) {
      case 1: // No filter
        fromDateUtc = "";
        toDateUtc = "";
        break;

      case 2: // Today
        {
          const startLocal = new Date(localNow.setHours(0, 0, 0, 0));
          const endLocal = new Date(startLocal);
          endLocal.setDate(endLocal.getDate() + 1);
          endLocal.setMilliseconds(endLocal.getMilliseconds() - 1);

          fromDateUtc = startLocal.toISOString();
          toDateUtc = endLocal.toISOString();
        }
        break;

      case 3: // This week (Mon–Sun)
        {
          const day = localNow.getDay();
          const diff = day === 0 ? 6 : day - 1;
          const weekStartLocal = new Date(localNow);
          weekStartLocal.setDate(localNow.getDate() - diff);
          weekStartLocal.setHours(0, 0, 0, 0);

          const weekEndLocal = new Date(weekStartLocal);
          weekEndLocal.setDate(weekEndLocal.getDate() + 7);
          weekEndLocal.setMilliseconds(weekEndLocal.getMilliseconds() - 1);

          fromDateUtc = weekStartLocal.toISOString();
          toDateUtc = weekEndLocal.toISOString();
        }
        break;

      case 4: // This month
        {
          const monthStartLocal = new Date(
            localNow.getFullYear(),
            localNow.getMonth(),
            1,
            0,
            0,
            0,
            0
          );
          const monthEndLocal = new Date(monthStartLocal);
          monthEndLocal.setMonth(monthEndLocal.getMonth() + 1);
          monthEndLocal.setMilliseconds(monthEndLocal.getMilliseconds() - 1);

          fromDateUtc = monthStartLocal.toISOString();
          toDateUtc = monthEndLocal.toISOString();
        }
        break;

      case 5: // This year
        {
          const yearStartLocal = new Date(
            localNow.getFullYear(),
            0,
            1,
            0,
            0,
            0,
            0
          );
          const yearEndLocal = new Date(yearStartLocal);
          yearEndLocal.setFullYear(yearEndLocal.getFullYear() + 1);
          yearEndLocal.setMilliseconds(yearEndLocal.getMilliseconds() - 1);

          fromDateUtc = yearStartLocal.toISOString();
          toDateUtc = yearEndLocal.toISOString();
        }
        break;

      case 6: // Custom range
        {
          const startLocal = new Date(`${startDate}T00:00:00`);
          const endLocal = new Date(`${endDate}T23:59:59.999`);

          fromDateUtc = startLocal.toISOString();
          toDateUtc = endLocal.toISOString();
        }
        break;

      default:
        {
          const nextWeekEnd = new Date(localNow);
          const daysUntilEndOfNextWeek = ((7 - localNow.getDay()) % 7) + 7;
          nextWeekEnd.setDate(localNow.getDate() + daysUntilEndOfNextWeek + 1);
          nextWeekEnd.setHours(23, 59, 59, 999);

          fromDateUtc = new Date("1976-01-01T00:00:00").toISOString();
          toDateUtc = nextWeekEnd.toISOString();
        }
        break;
    }

    return { fromDateUtc, toDateUtc };
  };
  // Debounce using useRef for memory management
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const debounce = (func: () => void, delay: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(func, delay);
  };

  // Handle Option Change for fetch data
  const handleOptionChange = (option: Option | null) => {
    if (!option) return;
    // Update selected option
    if (option.id === 6 && startDate && endDate) {
      const formattedLabel = `${startDate} - ${endDate}`;
      const customOption = { ...option, label: formattedLabel };
      setSelectedOption(customOption);
    } else {
      setSelectedOption(option);
    }
    // setSelectedOption(option);
    if (option.id === 6) {
      setStartDate(null);
      setEndDate(null);
    }
    debounce(async () => {
      if (option.id === 6 && (!startDate || !endDate)) {
        console.log("Waiting for date selection...");
        return;
      }

      // const userDetails = authService.decryptData("UserDetails", 2);

      // const isAllSite = userDetails.lastAccess === -1 ? 1 : 0;

      // const requestData = {
      //   dateFilterId: option.id,
      //   startDate: option.id === 6 ? startDate : "",
      //   endDate: option.id === 6 ? endDate : "",
      //   isAllSite: isAllSite,
      // };
      const { fromDateUtc, toDateUtc } = getDateRangeUtc(
        option.id,
        startDate,
        endDate
      );
      const requestData = {
        dateFilterId: option.id,
        startDate: fromDateUtc,
        endDate: toDateUtc,
      };
      setIsLoading(true);
      try {
        const response = await dashboard.DashboardWeb(requestData);
        if (response.success) {
          console.log("response :" + response);
          const dashboards = response.data.dashboards;
          if (dashboards.length > 0) {
            setCardsData(dashboards);
            console.log("dashboard data ", dashboards);
            const siteData = dashboards[0]; // using only the first site for now
            setLineChartData(siteData.lineChartData);
            // Extract unique status keys
            const keys = Array.from(
              new Set(
                siteData.lineChartData.flatMap((month: any) =>
                  month.data.map((d: any) => d.status)
                )
              )
            ) as string[];
            setDataKeys(keys);
            console.log("dashboard :", dashboards);
            // setLineChartData(dashboards[0].lineChartData);
          }
          mapDynmaicURL(baseApiUrlList, response.data?.apiEndPoints);
          authService.encryptData(baseApiUrlList, "serviceDetails", 1);
          // setCardsData(response.data.dashboards.cardsData);
          // setLineChartData(response.data.dashboards.lineChartData);
        } else {
          console.error("Error fetching data:", response.message);
        }
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setIsLoading(false);
      }
    }, 500);
  };

  const handleClearAndFetch = () => {
    handleOptionChange(durationOptions[0]);
  };

  const CustomLegend = () => {
    const allActive = activeKeys.length === dataKey.length;

    const toggleAll = () => {
      setActiveKeys(allActive ? [] : dataKey);
    };
    const toggleKey = (key: string) => {
      setActiveKeys((prev) => {
        const updated = prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key];

        // If all are removed, show all again
        return updated.length === 0 ? dataKey : updated;
      });
    };

    return (
      <div className="flex justify-center space-x-6 mt-4 mb-1">
        {/* "All" toggle */}
        <div
          className={`flex items-center cursor-pointer transition-opacity duration-200 ${
            allActive ? "" : "opacity-90"
          }`}
          onClick={toggleAll}
        >
          <div className="w-3 h-3 rounded-full bg-gray-600"></div>
          <span
            className={`ml-2 text-xl text-black-700 ${
              allActive ? "" : "line-through"
            }`}
          >
            All
          </span>
        </div>
        {dataKey.map((key, index) => {
          const isActive = activeKeys.includes(key);
          return (
            <div
              key={index}
              className={`flex items-center cursor-pointer transition-opacity duration-200 ${
                isActive ? "" : "opacity-90"
              }`}
              onClick={() => toggleKey(key)}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colorMap[key] }}
              ></div>
              <span
                className={`ml-2 text-xl text-black-700 ${
                  isActive ? "" : "line-through"
                }`}
              >
                {key}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const mainSite = cardsData?.[0] ?? { cardsData: [] };
  const otherSites = cardsData.slice(1);
  const formattedChartData = lineChartData.map((item) => {
    const statusObject: Record<string, number> = {};
    item.data.forEach(({ status, value }) => {
      statusObject[status] = value;
    });

    const formattedData: { month: string; [key: string]: number | string } = {
      month: item.label,
    };

    dataKey.forEach((key) => {
      formattedData[key] = statusObject[key] ?? 0;
    });

    return formattedData;
  });

  useEffect(() => {
    if (selectedOption?.id === 1) {
      console.log("Initial API call with All time filter.");
      handleOptionChange(selectedOption);
    }
    let siteConfigDetails = null;
    siteConfigDetails = authService.decryptData("siteConfigDetails", 2);
    siteConfigDetails = siteConfigDetails || "{}";
    let parsedReportData = JSON.parse(siteConfigDetails.reportDetails);
    setBarWithValue(parsedReportData?.dashboardBarWithValue);
  }, []);

  useEffect(() => {
    if (selectedOption?.id === 6 && startDate && endDate) {
      console.log("Both start and end dates are selected. Triggering API...");
      handleOptionChange(selectedOption);
    }
  }, [startDate, endDate, selectedOption]);

  // Refresh button click handler
  const handleRefresh = () => {
    if (selectedOption) {
      handleOptionChange(selectedOption); // 🔁 Re-trigger fetch
    }
  };

  // Function to check scroll position and update button visibility
  const checkScrollPosition = () => {
    if (cardContainerRef.current) {
      const container = cardContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };
  const scrollRight = () => {
    if (cardContainerRef.current) {
      cardContainerRef.current.scrollBy({ left: 180, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const scrollLeft = () => {
    if (cardContainerRef.current) {
      cardContainerRef.current.scrollBy({ left: -180, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  useEffect(() => {
    if (dataKey.length > 0 && activeKeys.length === 0) {
      setActiveKeys(dataKey); // Only run once to show all initially
      const newWidth = Math.min(130 + (dataKey.length - 1) * 50, 300);
      setChartWidth(newWidth);
    }
  }, [dataKey]);

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener("resize", checkScrollPosition);
    return () => window.removeEventListener("resize", checkScrollPosition);
  }, [mainSite?.cardsData]); // Or whatever triggers the card changes

  useEffect(() => {
    const element = chartContainerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect.width) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const barWidth = 66;
  const chartDataWidth = formattedChartData.length * barWidth;
  const finalChartWidth = Math.max(chartDataWidth, containerWidth);

  // const totalCard = mainSite.cardsData.find((card) => card.status === "Total");

  // const totalValue = totalCard?.value ?? 0;
  const handleExpandPreview = () => {
    setIsBarChartModalOpen((prev) => !prev);
  };
  function BarLabel(props: BarLabelProps) {
    if(barWithValue != 1 ){
      return null; 
    }
    const { color, x, y, width, ...otherProps } = props;

    return (
      <Text
        {...otherProps}
        fill={color}
        textAnchor="middle"
        x={x + width / 2}
        y={y - 8}
        fontSize="20px"
      />
    );
  }

  const Text = styled("text")<React.SVGProps<SVGTextElement>>(({ theme }) => ({
    ...theme?.typography?.body2,
    stroke: "none",
    fill: theme?.palette?.text?.primary,
    transition: "opacity 0.2s ease-in, fill 0.2s ease-in",
    textAnchor: "middle",
    dominantBaseline: "central",
    pointerEvents: "none",
    fontSize:12
  }));

  const series: BarSeriesType[] = dataKey.map((key) => ({
    type: "bar",
    id: key,
    label: key,
    color: colorMap[key] || d3.interpolateRainbow(Math.random()),
    data: formattedChartData.map((d) => Number(d[key] ?? 0)),
  }));
  const filteredSeries = series.filter(
    (s): s is BarSeriesType & { id: string } =>
      typeof s.id === "string" && activeKeys.includes(s.id)
  );

  return (
    <>
      {isLoading ? (
        <>
          {/* Header Skeletons */}
          <Skeleton width="15%" height={47} borderRadius={3} className="mb-4" />
          <div className="flex justify-end items-center space-x-4 mb-6">
            <Skeleton width="25rem" height={47} borderRadius={3} />
          </div>

          {/* Cards and Accordion */}
          <div className="flex flex-col lg:flex-row gap-6 w-full mt-3">
            {/* Left side: Cards + Accordion + Bar Chart */}
            <div className="lg:w-[72%] w-full space-y-8">
              <div className="relative w-full mb-12">
                {/* Horizontal Card Scroll Skeleton */}
                <div className="overflow-x-auto px-2">
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-nowrap gap-4 justify-start">
                      {[...Array(4)].map((_, index) => (
                        <Skeleton
                          key={index}
                          width={151}
                          height={163}
                          borderRadius={16}
                        />
                      ))}
                    </div>

                    {/* Accordion for other sites */}
                    {[...Array(2)].map((_, index) => (
                      <div
                        key={index}
                        className="rounded-[12px] border border-gray-300 bg-gray-100"
                      >
                        <div className="flex items-center px-4 py-3 gap-3 bg-[#D9E1E8] rounded-t-[12px]">
                          <Skeleton width={24} height={24} borderRadius="50%" />
                          <Skeleton width="20%" height={20} />
                        </div>
                        <div className="p-4">
                          <div className="flex flex-nowrap gap-4">
                            {[...Array(4)].map((_, i) => (
                              <Skeleton
                                key={i}
                                width={151}
                                height={163}
                                borderRadius={16}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bar Chart Skeleton */}
              <div className="mx-auto bg-[#F9FAFB] p-4 sm:p-6 rounded-2xl shadow-lg">
                <Skeleton width="25%" height={28} className="mb-4" />
                <div className="flex gap-4 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} width={80} height={20} />
                  ))}
                </div>
                <Skeleton width="100%" height={319} borderRadius={16} />
              </div>
            </div>

            {/* Right side: MultipleRadialBars-style skeleton */}
            <div className="lg:w-[28%] w-full bg-[#F9FAFB] shadow-md rounded-2xl p-4 h-full space-y-6">
              {/* Title Skeleton */}
              <Skeleton height={30} width="40%" className="mt-2" />

              {/* Chart Skeleton */}
              <div className="flex justify-center items-center h-[300px]">
                <Skeleton circle width={200} height={200} />
              </div>

              {/* Legend Skeleton */}
              <div className="flex flex-col items-center justify-center space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center space-x-4 w-full max-w-[300px]"
                  >
                    <Skeleton circle width={16} height={16} />
                    <Skeleton width="50%" height={20} />
                    <Skeleton width={40} height={20} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/*Flex layout starts here */}
          {!isBarChartModalOpen ? (
            <>
              <PageHeader
                title={t(pageTitle)}
                icon={<DashboardPageHeaderIcon />}
              />
              <div className="w-full flex justify-end items-center gap-4">
                <div className="w-[25rem]">
                  <SelectDuration
                    options={durationOptions}
                    labelName="Duration"
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isReset={false}
                    isDisabled={false}
                    handleChange={handleOptionChange}
                    defaultValue={selectedOption}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onClose={handleClearAndFetch}
                  />
                </div>

                <div className="h-10 w-[1px] bg-gray-300"></div>

                <IconTextButton
                  label="Refresh"
                  icon={<RefreshIcon />}
                  btnSize="large"
                  handleClick={handleRefresh}
                />
              </div>
              <div className="flex flex-col lg:flex-row gap-6 w-full mt-3 ">
                <div className="lg:w-[72%] w-full space-y-8">
                  {/* Card Section */}

                  <ScrollableCardBlock
                    site={mainSite}
                    otherSites={otherSites}
                    cardContainerRef={cardContainerRef}
                    scrollLeft={scrollLeft}
                    scrollRight={scrollRight}
                    showLeftButton={showLeftButton}
                    showRightButton={showRightButton}
                    expandedSite={expandedSite}
                    setExpandedSite={setExpandedSite}
                    cardHeight={"163px"}
                    cardWidth={"151px"}
                  />
                  {/* Bar Chart Section using MUI X */}

                  <div
                    ref={chartContainerRef}
                    className="mx-auto bg-[#F9FAFB] p-1 sm:p-6 rounded-2xl shadow-lg"
                  >
                    <div className="flex justify-between">
                      <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
                        Process Status
                      </h2>
                      {
                        filteredSeries.length > 0 && 
                        <IconBtn
                        size="w-[1.6rem]"
                        icon={<ExpandIcon />}
                        handleClick={handleExpandPreview}
                      />
                      }
                      
                    </div>
                    {/* Custom Legend */}
                    <CustomLegend />
                    {/* MUI X Bar Chart */}

                    <div className="w-full overflow-x-auto">
                     <div style={{ width: finalChartWidth }}>
                        <ChartContainer
                          dataset={formattedChartData}
                          xAxis={[{ scaleType: "band", dataKey: "month" }]}
                          series={filteredSeries}
                          height={400}
                          yAxis={[
                            {
                              width: 50,
                              valueFormatter: (value: number) => {
                                if (value >= 1000000)
                                  return (value / 1000000).toFixed(1) + "M";
                                if (value >= 1000)
                                  return (value / 1000).toFixed(1) + "k";
                                return value.toString();
                              },
                            },
                          ]}
                          margin={{ left: 0, right: 10 }}
                        >
                          <BarPlot
                            barLabel="value"
                            slots={{ barLabel: BarLabel }}
                          />
                          <ChartsXAxis />
                          <ChartsYAxis />
                          <ChartsTooltip />
                        </ChartContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:w-[28%] w-full">
                  <div>
                    {cardsData.length > 0 && (
                      <MultipleRadialBars
                        isSplitScreen={false}
                        cardContainerRef={cardContainerRef}
                        cardsData={cardsData[0].cardsData}
                        loading={isLoading}
                        size={300}
                        outerRadius={"80%"}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="h-full w-full bg-io-white z-50 p-[1.8rem]">
                <div className="w-full flex justify-between mx-[1rem]">
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
                    Process Status
                  </h2>
                  <div className = "pr-[1rem]">
                    <IconBtn
                    size="w-[1.6rem]"
                    icon={<PageModalCloseIcon />}
                    handleClick={handleExpandPreview}
                  />
                  </div>
                  
                </div>
                <div className="bg-[#F9FAFB] h-full rounded-2xl">
                  <div className="w-full h-full overflow-x-auto">
                    <ChartContainer
                      dataset={formattedChartData}
                      xAxis={[{ scaleType: "band", dataKey: "month" }]}
                      series={filteredSeries}
                      yAxis={[
                        {
                          width: 50,
                          valueFormatter: (value: number) => {
                            if (value >= 1000000)
                              return (value / 1000000).toFixed(1) + "M";
                            if (value >= 1000)
                              return (value / 1000).toFixed(1) + "k";
                            return value.toString();
                          },
                        },
                      ]}
                    >
                      <BarPlot
                        barLabel="value"
                        slots={{ barLabel: BarLabel }}
                      />
                      <ChartsXAxis />
                      <ChartsYAxis />
                      <ChartsTooltip />
                    </ChartContainer>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ReactDashboardUI;
